import { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../services/api';
import { workflowCache } from '../services/cacheService';
import { WorkflowExecution } from '../types';

interface UseOptimizedWorkflowStatusOptions {
  projectId: string;
  executionArn?: string;
  pollingInterval?: number;
  autoStart?: boolean;
  onStatusChange?: (status: string, workflow: WorkflowExecution) => void;
  onComplete?: (workflow: WorkflowExecution) => void;
  onError?: (error: string, workflow: WorkflowExecution) => void;
  enableCaching?: boolean;
  enableOptimisticUpdates?: boolean;
}

interface UseOptimizedWorkflowStatusReturn {
  workflow: WorkflowExecution | null;
  loading: boolean;
  error: string | null;
  startPolling: (executionArn: string) => void;
  stopPolling: () => void;
  isPolling: boolean;
  refresh: () => Promise<void>;
  pauseWorkflow: () => Promise<boolean>;
  resumeWorkflow: () => Promise<boolean>;
  cancelWorkflow: () => Promise<boolean>;
  retryWorkflow: () => Promise<string | null>;
  cacheStats: any;
}

export const useOptimizedWorkflowStatus = ({
  projectId,
  executionArn,
  pollingInterval = 3000,
  autoStart = true,
  onStatusChange,
  onComplete,
  onError,
  enableCaching = true,
  enableOptimisticUpdates = true
}: UseOptimizedWorkflowStatusOptions): UseOptimizedWorkflowStatusReturn => {
  const [workflow, setWorkflow] = useState<WorkflowExecution | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [currentExecutionArn, setCurrentExecutionArn] = useState<string | undefined>(executionArn);
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  const previousStatusRef = useRef<string | null>(null);
  const consecutiveErrorsRef = useRef(0);
  const lastSuccessfulFetchRef = useRef<number>(Date.now());

  // Adaptive polling based on workflow status and error count
  const getAdaptivePollingInterval = useCallback((status: string, errorCount: number) => {
    // Stop polling for completed workflows
    if (['SUCCEEDED', 'FAILED', 'TIMED_OUT', 'ABORTED'].includes(status)) {
      return 0;
    }

    // Increase interval if we're getting errors
    if (errorCount > 0) {
      return Math.min(pollingInterval * Math.pow(2, errorCount), 30000); // Max 30 seconds
    }

    // Normal polling for running workflows
    if (status === 'RUNNING') {
      return pollingInterval;
    }

    // Slower polling for unknown states
    return pollingInterval * 2;
  }, [pollingInterval]);

  const getCacheKey = useCallback((arn: string) => {
    return `workflow_status_${projectId}_${arn}`;
  }, [projectId]);

  const fetchWorkflowStatus = useCallback(async (arn: string, useCache = true) => {
    if (!mountedRef.current) return;
    
    const cacheKey = getCacheKey(arn);
    
    try {
      setError(null);
      
      let response;
      if (enableCaching && useCache) {
        // Use stale-while-revalidate caching strategy
        response = await workflowCache.getWithSWR(
          cacheKey,
          () => apiService.getWorkflowStatus(projectId, arn),
          30000, // 30 second TTL
          15000  // 15 second stale time
        );
      } else {
        response = await apiService.getWorkflowStatus(projectId, arn);
        if (enableCaching) {
          workflowCache.set(cacheKey, response, 30000);
        }
      }
      
      if (!mountedRef.current) return;
      
      if (response.success && response.data) {
        const workflowData: WorkflowExecution = {
          execution_arn: arn,
          execution_name: '',
          workflow_id: '',
          status: response.data.status as WorkflowExecution['status'],
          started_at: response.data.started_at || new Date().toISOString(),
          ended_at: response.data.ended_at,
          current_step: response.data.current_step,
          output: response.data.output,
          error: response.data.error,
          cause: response.data.cause
        };
        
        setWorkflow(workflowData);
        consecutiveErrorsRef.current = 0;
        lastSuccessfulFetchRef.current = Date.now();
        
        // Check for status changes
        if (previousStatusRef.current !== response.data.status) {
          onStatusChange?.(response.data.status, workflowData);
          previousStatusRef.current = response.data.status;
        }
        
        // Handle completion
        if (['SUCCEEDED', 'FAILED', 'TIMED_OUT', 'ABORTED'].includes(response.data.status)) {
          stopPolling();
          
          if (response.data.status === 'SUCCEEDED') {
            onComplete?.(workflowData);
          } else {
            onError?.(response.data.error || 'Workflow failed', workflowData);
          }
        } else {
          // Adjust polling interval based on status and error count
          const newInterval = getAdaptivePollingInterval(response.data.status, consecutiveErrorsRef.current);
          if (newInterval > 0 && newInterval !== pollingInterval && intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = setInterval(() => {
              fetchWorkflowStatus(arn, true);
            }, newInterval);
          }
        }
      } else {
        throw new Error(response.error || 'Failed to fetch workflow status');
      }
    } catch (err) {
      if (!mountedRef.current) return;
      
      consecutiveErrorsRef.current++;
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch workflow status';
      setError(errorMessage);
      onError?.(errorMessage, workflow!);
      
      // If we have too many consecutive errors, slow down polling significantly
      if (consecutiveErrorsRef.current >= 3) {
        const backoffInterval = Math.min(60000, pollingInterval * Math.pow(2, consecutiveErrorsRef.current));
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = setInterval(() => {
            fetchWorkflowStatus(arn, false); // Don't use cache on retry
          }, backoffInterval);
        }
      }
    }
  }, [projectId, onStatusChange, onComplete, onError, getAdaptivePollingInterval, pollingInterval, workflow, getCacheKey, enableCaching]);

  const startPolling = useCallback((arn: string) => {
    setCurrentExecutionArn(arn);
    setIsPolling(true);
    setLoading(true);
    previousStatusRef.current = null;
    consecutiveErrorsRef.current = 0;
    
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    // Check cache first for immediate response
    if (enableCaching) {
      const cached = workflowCache.get(getCacheKey(arn));
      if (cached && typeof cached === 'object' && 'success' in cached && cached.success && 'data' in cached && cached.data) {
        const workflowData: WorkflowExecution = {
          execution_arn: arn,
          execution_name: '',
          workflow_id: '',
          status: (cached.data as any).status as WorkflowExecution['status'],
          started_at: (cached.data as any).started_at || new Date().toISOString(),
          ended_at: (cached.data as any).ended_at,
          current_step: (cached.data as any).current_step,
          output: (cached.data as any).output,
          error: (cached.data as any).error,
          cause: (cached.data as any).cause
        };
        setWorkflow(workflowData);
        setLoading(false);
      }
    }
    
    // Fetch immediately (will use cache if available)
    fetchWorkflowStatus(arn, true).finally(() => {
      if (mountedRef.current) {
        setLoading(false);
      }
    });
    
    // Set up polling interval
    intervalRef.current = setInterval(() => {
      fetchWorkflowStatus(arn, true);
    }, pollingInterval);
  }, [fetchWorkflowStatus, pollingInterval, enableCaching, getCacheKey]);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const refresh = useCallback(async () => {
    if (currentExecutionArn) {
      setLoading(true);
      // Force refresh without cache
      await fetchWorkflowStatus(currentExecutionArn, false);
      setLoading(false);
    }
  }, [currentExecutionArn, fetchWorkflowStatus]);

  // Optimistic workflow control methods
  const pauseWorkflow = useCallback(async (): Promise<boolean> => {
    if (!currentExecutionArn) return false;
    
    try {
      if (enableOptimisticUpdates && workflow) {
        // Optimistically update the workflow status
        const optimisticWorkflow = { ...workflow, status: 'PAUSED' as const };
        setWorkflow(optimisticWorkflow);
      }
      
      const response = await apiService.pauseWorkflow(projectId, currentExecutionArn);
      if (response.success) {
        await refresh();
        return true;
      } else {
        // Revert optimistic update on failure
        if (enableOptimisticUpdates) {
          await refresh();
        }
        return false;
      }
    } catch (error) {
      // Revert optimistic update on error
      if (enableOptimisticUpdates) {
        await refresh();
      }
      return false;
    }
  }, [currentExecutionArn, projectId, refresh, enableOptimisticUpdates, workflow]);

  const resumeWorkflow = useCallback(async (): Promise<boolean> => {
    if (!currentExecutionArn) return false;
    
    try {
      if (enableOptimisticUpdates && workflow) {
        // Optimistically update the workflow status
        const optimisticWorkflow = { ...workflow, status: 'RUNNING' as const };
        setWorkflow(optimisticWorkflow);
      }
      
      const response = await apiService.resumeWorkflow(projectId, currentExecutionArn);
      if (response.success) {
        await refresh();
        return true;
      } else {
        // Revert optimistic update on failure
        if (enableOptimisticUpdates) {
          await refresh();
        }
        return false;
      }
    } catch (error) {
      // Revert optimistic update on error
      if (enableOptimisticUpdates) {
        await refresh();
      }
      return false;
    }
  }, [currentExecutionArn, projectId, refresh, enableOptimisticUpdates, workflow]);

  const cancelWorkflow = useCallback(async (): Promise<boolean> => {
    if (!currentExecutionArn) return false;
    
    try {
      if (enableOptimisticUpdates && workflow) {
        // Optimistically update the workflow status
        const optimisticWorkflow = { ...workflow, status: 'ABORTED' as const };
        setWorkflow(optimisticWorkflow);
      }
      
      const response = await apiService.cancelWorkflow(projectId, currentExecutionArn);
      if (response.success) {
        stopPolling();
        await refresh();
        return true;
      } else {
        // Revert optimistic update on failure
        if (enableOptimisticUpdates) {
          await refresh();
        }
        return false;
      }
    } catch (error) {
      // Revert optimistic update on error
      if (enableOptimisticUpdates) {
        await refresh();
      }
      return false;
    }
  }, [currentExecutionArn, projectId, refresh, stopPolling, enableOptimisticUpdates, workflow]);

  const retryWorkflow = useCallback(async (): Promise<string | null> => {
    if (!currentExecutionArn) return null;
    
    try {
      const response = await apiService.retryWorkflow(projectId, currentExecutionArn);
      if (response.success && response.data) {
        // Start polling the new execution
        startPolling(response.data.execution_arn);
        return response.data.execution_arn;
      }
      return null;
    } catch (error) {
      return null;
    }
  }, [currentExecutionArn, projectId, startPolling]);

  // Auto-start polling if executionArn is provided and autoStart is true
  useEffect(() => {
    if (executionArn && autoStart) {
      startPolling(executionArn);
    }
    
    return () => {
      stopPolling();
    };
  }, [executionArn, autoStart, startPolling, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  // Visibility change handling - pause polling when tab is not visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isPolling) {
        // Tab is hidden, slow down polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = setInterval(() => {
            fetchWorkflowStatus(currentExecutionArn!, true);
          }, pollingInterval * 3); // 3x slower when hidden
        }
      } else if (!document.hidden && isPolling) {
        // Tab is visible, resume normal polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = setInterval(() => {
            fetchWorkflowStatus(currentExecutionArn!, true);
          }, pollingInterval);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isPolling, currentExecutionArn, pollingInterval, fetchWorkflowStatus]);

  return {
    workflow,
    loading,
    error,
    startPolling,
    stopPolling,
    isPolling,
    refresh,
    pauseWorkflow,
    resumeWorkflow,
    cancelWorkflow,
    retryWorkflow,
    cacheStats: workflowCache.getStats()
  };
};